import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { TableChart, PictureAsPdf, Code } from '@mui/icons-material';
import ThemeToggle from '../components/ThemeToggle';
import { exportAnalyticsToExcel, exportAnalyticsToPDF } from '../utils/exportUtils';

interface GlobalAnalytics {
  total_users: number;
  active_users_30d: number;
  total_projects: number;
  total_tokens_consumed: number;
  total_tokens_remaining: number;
  avg_score: number;
  avg_session_duration: number;
  activity_by_type: Array<{ activity_type: string; count: number }>;
  top_features: Array<{ feature_name: string; total_uses: number; total_tokens: number }>;
  language_distribution: Array<{ report_language: string; count: number }>;
  project_type_distribution: Array<{ project_type: string; count: number }>;
  country_distribution: Array<{ country: string; count: number }>;
  new_users_by_month: Array<{ month: string; new_users: number }>;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [analytics, setAnalytics] = useState<GlobalAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getGlobalAnalytics();
      setAnalytics(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch analytics');
      if (err.response?.status === 403) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-4 px-4 py-2 bg-gray-900 dark:bg-white text-white rounded hover:bg-gray-800 dark:hover:bg-gray-100"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} dk`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">Admin Dashboard</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Sistem geneli istatistikler ve analitikler</p>
            </div>
            <div className="flex items-center gap-4">
              {analytics && (
                <>
                  <button
                    onClick={() => exportAnalyticsToExcel(analytics)}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium"
                    title="Export to Excel"
                  >
                    <TableChart style={{ width: '18px', height: '18px' }} />
                    <span>Excel</span>
                  </button>
                  <button
                    onClick={() => exportAnalyticsToPDF(analytics)}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                    title="Export to PDF"
                  >
                    <PictureAsPdf style={{ width: '18px', height: '18px' }} />
                    <span>PDF</span>
                  </button>
                </>
              )}
              <button
                onClick={() => navigate('/admin/cms')}
                className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors font-medium"
              >
                İçerik Yönetimi
              </button>
              <button
                onClick={() => navigate('/admin/users')}
                className="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors font-medium"
              >
                Kullanıcılar
              </button>
              <ThemeToggle size="sm" />
            </div>
          </div>
          <div className="mt-4 flex gap-4 flex-wrap">
            <button
              onClick={() => navigate('/admin/feedback')}
              className="px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
            >
              Kullanıcı Geri Bildirimleri
            </button>
            <button
              onClick={() => navigate('/admin/subscribers')}
              className="px-4 py-2 bg-amber-600 dark:bg-amber-500 text-white rounded-lg hover:bg-amber-700 dark:hover:bg-amber-600 transition-colors"
            >
              Mail Aboneleri
            </button>
            <button
              onClick={() => navigate('/development')}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-600 dark:bg-yellow-500 text-white rounded-lg hover:bg-yellow-700 dark:hover:bg-yellow-600 transition-colors"
            >
              <Code style={{ width: '18px', height: '18px' }} />
              <span>Geliştirme Sayfası</span>
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Toplam Kullanıcı</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{analytics.total_users}</div>
            <div className="text-sm text-green-600 dark:text-green-400 mt-1">
              {analytics.active_users_30d} aktif (30 gün)
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Toplam Proje</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{analytics.total_projects}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Ort. Puan: {analytics.avg_score.toFixed(1)}/10
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Token Kullanımı</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {analytics.total_tokens_consumed.toLocaleString()}
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              {analytics.total_tokens_remaining.toLocaleString()} kalan
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Ort. Oturum Süresi</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {formatDuration(analytics.avg_session_duration)}
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity by Type */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white mb-4">Aktivite Türleri (30 Gün)</h2>
            <div className="space-y-3">
              {analytics.activity_by_type.slice(0, 5).map((activity) => (
                <div key={activity.activity_type} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300 capitalize">{activity.activity_type.replace('_', ' ')}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-gray-600 dark:bg-gray-400 h-2 rounded-full"
                        style={{
                          width: `${(activity.count / analytics.activity_by_type[0].count) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-12 text-right">
                      {activity.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Features */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white mb-4">En Çok Kullanılan Özellikler</h2>
            <div className="space-y-3">
              {analytics.top_features.slice(0, 5).map((feature) => (
                <div key={feature.feature_name} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300 capitalize">
                    {feature.feature_name.replace('_', ' ')}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{feature.total_uses} kullanım</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {feature.total_tokens} token
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Language Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white mb-4">Dil Dağılımı</h2>
            <div className="space-y-3">
              {analytics.language_distribution.map((lang) => (
                <div key={lang.report_language} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{lang.report_language}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-green-600 dark:bg-green-500 h-2 rounded-full"
                        style={{
                          width: `${(lang.count / analytics.language_distribution[0].count) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-12 text-right">
                      {lang.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Type Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white mb-4">Proje Tür Dağılımı</h2>
            <div className="space-y-3">
              {analytics.project_type_distribution.map((type) => (
                <div key={type.project_type} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{type.project_type}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full"
                        style={{
                          width: `${
                            (type.count / analytics.project_type_distribution[0].count) * 100
                          }%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-12 text-right">
                      {type.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Country Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white mb-4">
              Ülke Dağılımı (30 Gün)
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                ({analytics.country_distribution.length} ülke)
              </span>
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {analytics.country_distribution.length > 0 ? (
                analytics.country_distribution.map((country, index) => {
                  const maxCount = analytics.country_distribution[0].count;
                  const percentage = maxCount > 0 ? (country.count / maxCount) * 100 : 0;
                  return (
                    <div key={country.country || `unknown-${index}`} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-gray-700 dark:text-gray-300 truncate">
                          {country.country || 'Bilinmiyor'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-gray-600 dark:bg-gray-400 h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-12 text-right">
                          {country.count}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  Ülke verisi bulunamadı
                </div>
              )}
            </div>
          </div>

          {/* New Users by Month */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700 p-6">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white mb-4">Yeni Kullanıcılar (Aylık)</h2>
            <div className="space-y-3">
              {analytics.new_users_by_month.map((month) => (
                <div key={month.month} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{month.month}</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{month.new_users} kullanıcı</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
